/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ADS_ENABLED } from '../config'
import { isGdprHideAdsActive } from '../util/isGdprDncHideAdsActive'
import $, { Cash, Selector } from 'cash-dom'
import isSupporter from '../util/isSupporter'
import logGoogleEvent from '../util/logEvent'

// Define window interface to extend global types
interface Window {
	clearTimeout(id: number): void
	setTimeout(callback: () => void, ms: number): number
	__VM: ((admanager: any, scope?: any) => void)[] // Prosper array
}

// Extend window with our custom types
declare const window: Window & typeof globalThis & Selector

// Initialize Prosper array if it doesn't exist
window.__VM = window.__VM || []

//==================================================================================================
// Add a page session tracker to ensure accurate RPM data reports
window.__VM.push((_, scope) => {
	scope.Instances.pageManager.newPageSession(false)
})

//==================================================================================================

// Interface for tracking ad instances
interface AdInstances {
	ows_desktopbottombanner?: any // prev. bottomBanner
	ows_mobilebottombanner?: any // prev. mainGameAdMobile
	ows_vertical?: any // prev. verticalSticky
}

type AdType = keyof AdInstances

// Object to store active ad instances
const adInstances: AdInstances = {}

// Timer for handling resize events
let resizeTimeout = 0

// Track window dimensions to detect changes
let dimensions = ''

/**
 * Debounced function to refresh ads on resize
 */
export function refreshVenatusAds(): void {
	window.clearTimeout(resizeTimeout)
	resizeTimeout = window.setTimeout(renderVenatusAds, 300)
}

/**
 * Main function to render ads based on screen size and device type
 */
export async function renderVenatusAds(): Promise<void> {
	const width = $('body').width()
	const height = $('body').height()

	const bottomBannerContainer = $('.bottom-banner')

	// Check if ads should be shown
	const adsEnabled = ADS_ENABLED
	// const isMobile = Boolean(navigator.userAgent.match(/(iPhone|iPod|iPad|Android|BlackBerry|IEMobile)/))
	// const isDesktop = !isMobile
	const minHeight = 480

	const showAds = adsEnabled && !isSupporter() && height > minHeight && !isGdprHideAdsActive()

	// If ads shouldn't be shown, remove all and return
	if (!showAds) {
		removeAllAds()
		return
	}

	// Determine which ads to show based on screen size and device
	const showVerticalAds = width >= 728
	const showBottomBanner = width >= 728 && height >= 700
	const showMobileAd = !showBottomBanner && height >= 515

	// Update body classes for styling
	$('body').toggleClass('venatus--mobile', showMobileAd)

	// Handle bottom banner
	if (showBottomBanner) {
		insertAd(bottomBannerContainer, 'ows_desktopbottombanner')
	} else {
		removeAd('ows_desktopbottombanner', true, bottomBannerContainer)
	}

	// Handle vertical sticky and video slider ads
	const dimensionsString = () => `${$(window).width()}x${$(window).height()}`
	const dimensionsHaveChanged = dimensions !== dimensionsString()

	if (dimensionsHaveChanged) {
		removeAd('ows_vertical')

		if (showVerticalAds) {
			insertAd(null, 'ows_vertical')
		}
		dimensions = dimensionsString()
	}

	// Handle mobile game ads
	if (showMobileAd) {
		insertAd($('body').find('.game-adbox'), 'ows_mobilebottombanner')
	} else {
		removeAd('ows_mobilebottombanner')
	}
}

/**
 * Inserts an ad into the specified container
 * @param container - jQuery element to insert the ad into
 * @param adType - Type of ad to insert
 */
export function insertAd(container: Cash | null, adType: AdType): void {
	if (container) container.show()

	// If there is a style rule which is hiding the container (which must be with !important),
	// then don't place an ad in this container.
	if (
		container &&
		container[0] instanceof Element &&
		getComputedStyle(container[0]).display === 'none'
	) {
		return
	}

	if (adType === 'ows_mobilebottombanner') {
		window.__VM.push((admanager, scope) => {
			adInstances.ows_mobilebottombanner = scope.Config.get(
				'mobile_banner',
				'ows_mobilebottombanner'
			).display({
				query: 'body > div#root > div.app > div.game > div.game-adbox',
			})
		})
	}

	if (adType === 'ows_desktopbottombanner') {
		window.__VM.push((admanager, scope) => {
			adInstances.ows_desktopbottombanner = scope.Config.get(
				'leaderboard',
				'ows_desktopbottombanner'
			).display({
				query: 'body > div#root > div.bottom-banner',
			})
		})
	}

	if (adType === 'ows_vertical') {
		window.__VM.push((admanager, scope) => {
			// Special case for vertical sticky - no container needed
			adInstances.ows_vertical = scope.Config.verticalSticky().display('ows_vertical')
		})
	}
}

/**
 * Removes a specific ad
 * @param adType - Type of ad to remove
 * @param hide - Whether to hide the container after removal
 * @param container - Optional container to hide
 */
export function removeAd(adType: AdType, hide = false, container?: Cash): void {
	window.__VM.push((admanager, scope) => {
		const instance = adInstances[adType]

		switch (adType) {
			case 'ows_vertical':
				// Special case for vertical sticky
				scope.Config.verticalSticky().destroy()
				break
			default:
				// Remove standard ad placements
				if (instance) {
					admanager.removePlacement(instance.instance())
					adInstances[adType] = undefined
				}
				break
		}
	})

	if (container && hide) {
		container.hide()
	}
}

/**
 * Removes all ad placements and clears instances
 */
export function removeAllAds(): void {
	window.__VM.push(admanager => {
		// Remove all placements from Venatus
		admanager.removeAllPlacements()

		// Clear our instance tracking
		// eslint-disable-next-line no-restricted-syntax, guard-for-in
		for (const key in adInstances) {
			adInstances[key as keyof AdInstances] = undefined
		}
	})

	// Hide ad containers
	$('.ad.bottom-banner').hide()
}

/**
 * Function to inject the ads script. This is responsible for adding the
 * consent popup functionality to the page, which in turn also impacts on
 * Google Analytics tracking.
 */
export function injectAdsAndCMPScript() {
	// Venatus ad script for v4
	const adBlock = `<script id="adscript" src="https://hb.vntsm.com/v4/live/vms/sites/puzzlist.com/index.js" async></script>`

	// Insert Venatus script if not present
	if ($('#adscript').length === 0) {
		$('head').append($(adBlock))
		logGoogleEvent('venatus_ads')
	}
}
